// Pure helpers that turn templates + selected/random questions into the
// `parts[]` paper JSON consumed by <QuestionPaperTemplate />.

const BLANK_LIST = "________________________________  (slot not filled)";
const BLANK_EO = "____________________  (slot not filled)";

const ROMAN = ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x", "xi", "xii"];
export const romanLabel = (i) => ROMAN[i] || `${i + 1}`;

// MCQ options arrive as a newline-separated string ("(a) ...\n(b) ..."); split to lines.
export const splitOptions = (options) =>
  options ? String(options).split("\n").map((o) => o.trim()).filter(Boolean) : [];

export function shuffle(input) {
  const a = input.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Working sections (with empty selections) from a saved template's parts[].
export function sectionsFromTemplate(template) {
  return (template?.parts || []).map((p) => ({
    key: p.key || p.partkey,
    title: p.title,
    pattern: p.pattern,
    count: p.count,
    marks: p.marks,
    start: p.start,
    splitMark: p.split_mark || [],
    instructions: p.instructions || [],
    pick: p.pick || {},
    choice: p.choice || null,
    items: [],
  }));
}

// Rebuild AddQuestionsStep working-sections (with their picked questions) from a
// saved paper's ViewQuestionPaper sections — used to edit a FIXED paper like the
// create/add screen. Mirrors the item shapes AddQuestionsStep produces.
export function sectionsFromSavedPaper(apiSections) {
  const norm = (q) =>
    q
      ? {
          id: q.quiz_id,
          paper_question_id: q.paper_question_id,
          text: q.question,
          marks: q.marks,
          options: q.options,
          image_url: q.image_url,
          question_type: q.question_type,
          level: q.level,
          blooms_level: q.blooms_level,
          subcategory: q.subcategory,
        }
      : null;
  return (apiSections || []).map((s) => {
    const splitMark = s.split_mark || [];
    const isSplit = splitMark.length > 1;
    const qs = [...(s.questions || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const groupSize = isSplit ? splitMark.length : 1;
    let items = [];
    if (s.pattern === "either-or") {
      if (qs.some((q) => q.pair_id != null)) {
        const byPair = {};
        const order = [];
        qs.forEach((q) => {
          if (!(q.pair_id in byPair)) { byPair[q.pair_id] = { a: [], b: [] }; order.push(q.pair_id); }
          byPair[q.pair_id][(q.pair_label || "a").toLowerCase() === "b" ? "b" : "a"].push(norm(q));
        });
        items = order.map((pid) => {
          const { a, b } = byPair[pid];
          return isSplit ? { a: { subs: a }, b: { subs: b } } : { a: a[0] || null, b: b[0] || null };
        });
      } else {
        const perSlot = groupSize * 2;
        for (let i = 0; i < qs.length; i += perSlot) {
          const c = qs.slice(i, i + perSlot);
          items.push(
            isSplit
              ? { a: { subs: c.slice(0, groupSize).map(norm) }, b: { subs: c.slice(groupSize, perSlot).map(norm) } }
              : { a: norm(c[0]), b: norm(c[1]) }
          );
        }
      }
    } else if (isSplit) {
      for (let i = 0; i < qs.length; i += groupSize) items.push({ subs: qs.slice(i, i + groupSize).map(norm) });
    } else {
      items = qs.map(norm);
    }
    // Required number of slots (template intent) vs items.length (what's filled).
    const perSlot = s.pattern === "either-or" ? groupSize * 2 : groupSize;
    const requiredSlots = perSlot ? Math.round((s.no_of_questions || 0) / perSlot) : items.length;
    return {
      key: `sec_${s.paper_section_id}`,
      paper_section_id: s.paper_section_id,
      title: s.section_name,
      pattern: s.pattern,
      count: requiredSlots || items.length,
      marks: isSplit ? splitMark.reduce((a, b) => a + b, 0) : qs[0]?.marks || s.marks_per_question || 0,
      start: s.start_number,
      splitMark,
      instructions: s.instructions || [],
      pick: s.pick_filters || {},
      choice: s.choice || null,
      items,
    };
  });
}

export function instructionsToText(instructions) {
  if (Array.isArray(instructions)) return instructions.join("\n");
  return typeof instructions === "string" ? instructions : "";
}

export function instructionsToList(text) {
  const value = instructionsToText(text).replace(/\s+$/, ""); // drop trailing blank lines only
  return value ? value.split("\n") : [];
}

// Heading block — template header_defaults override sensible fallbacks, and a
// few live form values (subject/time) override both. Reads either the backend
// snake_case keys (exam_title, max_marks…) or the older camelCase keys.
export function buildHeader(form, template) {
  const hd = (template && template.header_defaults) || {};
  return {
    university: hd.university || "",
    examTitle: hd.exam_title || hd.examTitle || "B.E / B.Tech Degree Examination",
    collegeName: hd.college_name || hd.collegeName || "College of Engineering",
    department: form.department || hd.department || "Computer Science and Engineering",
    subjectCode: form.subjectCode || hd.subject_code || hd.subjectCode || "—",
    subjectName: form.subjectName || hd.subject_name || hd.subjectName || "—",
    // Regulation / Semester / Academic Year are shown ONLY when supplied — no defaults.
    semester: hd.semester || "",
    regulation: hd.regulation || "",
    academicYear: form.academicYear || hd.academic_year || hd.academicYear || "",
    time: form.time || hd.time || "180 Minutes",
    maxMarks: hd.max_marks || hd.maxMarks || form.maxMarks || 100,
    footerNote: hd.footer_note || hd.footerNote || "All the Best!",
    instructions: instructionsToList(hd.instructions),
  };
}

function partInstructions(part) {
  const instructions = [...(part.instructions || [])];
  if (part.choice) instructions.push(`Answer any ${part.choice.must_answer} of ${part.choice.total_given} questions`);
  return instructions;
}

function marksLine(part) {
  const total = (part.count || 0) * (part.marks || 0);
  return `(${part.count} x ${part.marks} = ${total} Marks)`;
}

// FIXED — build from the teacher's manually selected questions.
export function buildPaperFromSections(sections, header) {
  const parts = [];
  let nextNum = 1;
  sections.forEach((sec) => {
    const start = sec.start || nextNum;
    const instructions = partInstructions(sec);
    if (sec.pattern === "list") {
      const splitMark = sec.splitMark || [];
      const isSplit = splitMark.length > 1;
      const questions = [];
      for (let i = 0; i < sec.count; i++) {
        const item = sec.items[i];
        if (isSplit) {
          // Each slot is a group of sub-questions (i, ii, …), one per split mark.
          questions.push({
            subs: splitMark.map((m, si) => ({
              text: item?.subs?.[si]?.text || BLANK_LIST,
              options: item?.subs?.[si]?.options,
              image: item?.subs?.[si]?.image_url || item?.subs?.[si]?.image || null,
              marks: m,
            })),
          });
        } else {
          questions.push(item ? { text: item.text, options: item.options, image: item.image_url || item.image || null, marks: item.marks != null ? item.marks : sec.marks } : BLANK_LIST);
        }
      }
      parts.push({ title: sec.title, pattern: "list", marksLine: marksLine(sec), instructions, questions, splitMark, start });
    } else {
      const eoSplitMark = sec.splitMark || [];
      const eoSplit = eoSplitMark.length > 1;
      // An option (a/b) is either a single question or, when split, a group of sub-questions.
      const buildOpt = (opt) =>
        eoSplit
          ? { subs: eoSplitMark.map((m, si) => ({ text: opt?.subs?.[si]?.text || BLANK_EO, options: opt?.subs?.[si]?.options, image: opt?.subs?.[si]?.image_url || opt?.subs?.[si]?.image || null, marks: m })) }
          : opt ? { text: opt.text, options: opt.options, image: opt.image_url || opt.image || null } : { text: BLANK_EO };
      const questions = [];
      for (let i = 0; i < sec.count; i++) {
        const pr = sec.items[i];
        questions.push({ number: start + i, a: buildOpt(pr?.a), b: buildOpt(pr?.b) });
      }
      parts.push({ title: sec.title, pattern: "either-or", marksPerQuestion: sec.marks, marksLine: marksLine(sec), instructions, questions, splitMark: eoSplitMark });
    }
    nextNum = start + sec.count;
  });
  return { ...header, parts };
}

// RANDOM — build from a fetched question pool per part (shuffled here).
export function buildRandomPaper(templateParts, poolByIndex, header) {
  const parts = [];
  let nextNum = 1;
  (templateParts || []).forEach((part, idx) => {
    const pool = shuffle(poolByIndex[idx] || []);
    const start = part.start || nextNum;
    const instructions = partInstructions(part);
    if (part.pattern === "list") {
      const questions = [];
      for (let i = 0; i < part.count; i++) questions.push(pool[i] ? { text: pool[i].text, options: pool[i].options, image: pool[i].image_url || pool[i].image || null, marks: pool[i].marks != null ? pool[i].marks : part.marks } : BLANK_LIST);
      parts.push({ title: part.title, pattern: "list", marksLine: marksLine(part), instructions, questions, splitMark: part.split_mark || [], start });
    } else {
      const questions = [];
      let pi = 0;
      for (let i = 0; i < part.count; i++) {
        const a = pool[pi++], b = pool[pi++];
        questions.push({
          number: start + i,
          a: a ? { text: a.text, options: a.options, image: a.image_url || a.image || null } : { text: BLANK_EO },
          b: b ? { text: b.text, options: b.options, image: b.image_url || b.image || null } : { text: BLANK_EO },
        });
      }
      parts.push({ title: part.title, pattern: "either-or", marksPerQuestion: part.marks, marksLine: marksLine(part), instructions, questions, splitMark: part.split_mark || [] });
    }
    nextNum = start + part.count;
  });
  return { ...header, parts };
}

// ---- RANDOM (pool-based) helpers ----

// Number of question slots a section must fill (drives pool-count validation).
// Mirrors the backend handoff formula.
export function slotCount(part) {
  if (!part) return 0;
  const sm = part.split_mark || part.splitMark || [];
  const n = sm.length;
  if (part.pattern === "either-or") {
    // Split takes priority over choice: each pair contributes option_a + option_b,
    // each split into n sub-parts → 2 × n × count physical slots (incl. split+choice, e.g. Part C).
    if (n) return 2 * n * part.count;
    // Choice without split: the pool only needs total_given pair-slots.
    if (part.choice && part.choice.total_given) return part.choice.total_given;
    // Plain either-or: option_a + option_b per pair.
    return 2 * part.count;
  }
  // "list" (and default)
  return n ? part.count * n : part.count;
}

// Preview/student-delivery options arrive as [{label,text}] (or a string);
// the printable renderer expects a newline-joined "(A) text" string.
function optionsToString(options) {
  if (!options) return null;
  if (Array.isArray(options)) {
    return options
      .map((o) => (typeof o === "string" ? o : `(${o.label}) ${o.text}`))
      .join("\n");
  }
  return options; // already a string
}

// RANDOM — turn a /PreviewRandomPaper response (same shape as QuestionsForStudent:
// sections[].questions[] with type single|split|either-or|either-or-split) into the
// `parts[]` paper JSON consumed by <QuestionPaperTemplate />. `headerOverride` carries
// live form values (subject/time/etc.) that win over the API header.
export function buildPaperFromPreview(apiData, headerOverride = {}) {
  const h = apiData?.header || {};
  const header = {
    university: h.university || "",
    examTitle: h.exam_title || "B.E / B.Tech Degree Examination",
    collegeName: h.college_name || "College of Engineering",
    department: headerOverride.department || h.department || "—",
    subjectCode: headerOverride.subjectCode || h.subject_code || "—",
    subjectName: headerOverride.subjectName || h.subject_name || "—",
    // Regulation / Semester / Academic Year are shown ONLY when supplied — no defaults.
    semester: h.semester || "",
    regulation: h.regulation || "",
    academicYear: headerOverride.academicYear || h.academic_year || "",
    time: headerOverride.time || (h.time ? `${h.time}` : "—"),
    maxMarks: h.max_marks || 0,
    footerNote: h.footer_note || "All the Best!",
  };

  const buildOpt = (opt) =>
    opt
      ? { text: opt.question, options: optionsToString(opt.options), image: opt.image_url || null }
      : { text: BLANK_EO };

  const parts = (apiData?.sections || []).map((sec) => {
    const marks = sec.marks_per_question || 0;
    const start = sec.start_number || 1;
    const instructions = sec.instructions || [];
    const sm = sec.split_mark || [];
    // A split option/question holds its sub-parts as an array directly
    // (option_a: [{sub_label, question, marks}, ...]) — fall back to .sub_parts just in case.
    const toSubs = (val) => ({
      subs: (Array.isArray(val) ? val : val?.sub_parts || []).map((sp, si) => ({
        text: sp.question,
        options: optionsToString(sp.options),
        image: sp.image_url || null,
        marks: sm[si] != null ? sm[si] : sp.marks,
      })),
    });

    if (sec.pattern === "either-or") {
      const questions = (sec.questions || []).map((q) => {
        if (q.type === "either-or-split" || Array.isArray(q.option_a)) {
          return { number: q.question_number, a: toSubs(q.option_a), b: toSubs(q.option_b) };
        }
        return { number: q.question_number, a: buildOpt(q.option_a), b: buildOpt(q.option_b) };
      });
      const total = questions.length * marks;
      return { title: sec.section_name, pattern: "either-or", marksPerQuestion: marks, marksLine: `(${questions.length} x ${marks} = ${total} Marks)`, instructions, questions, splitMark: sm };
    }
    // list (single / split)
    const questions = (sec.questions || []).map((q) => {
      if (q.type === "split" || Array.isArray(q.sub_parts)) {
        return toSubs(q.sub_parts);
      }
      return { text: q.question, options: optionsToString(q.options), image: q.image_url || null, marks: q.marks != null ? q.marks : marks };
    });
    const total = questions.length * marks;
    return { title: sec.section_name, pattern: "list", marksLine: `(${questions.length} x ${marks} = ${total} Marks)`, instructions, questions, splitMark: sec.split_mark || [], start };
  });

  return { ...header, parts };
}

// Summary line for a template card / chooser.
export function templateSummary(t) {
  const parts = (t.parts || []).map((p) => {
    // Show the per-question mark split (e.g. 13 → [6+7]) when the part defines one.
    const split =
      Array.isArray(p.split_mark) && p.split_mark.length ? ` [${p.split_mark.join("+")}]` : "";
    const choice = p.choice ? ` (Answer ${p.choice.must_answer} of ${p.choice.total_given})` : "";
    return (
      `${p.title}: ${p.count}×${p.marks} ${p.pattern === "list" ? "List" : "Either-Or"}` +
      split +
      choice
    );
  }).join("  |  ");
  const total = (t.parts || []).reduce((s, p) => s + (p.choice ? p.choice.must_answer : p.count) * p.marks, 0);
  return { parts, total };
}

export const displayName = (t) => t?.template_name || t?.name || "";
